import { useEffect, type FC } from 'react';
import { DynamicField, DynamicFieldset, Space, Text } from '@g2u/ui-kit';
import { CheckboxOptionType, Divider, Form, type FormInstance, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { isArray, isString } from 'lodash';
import cx from 'classnames';

import { CallbackSource, JobServiceType, type JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { ProductQuestion, ProductList } from '.';
import { useText } from 'helpers/hooks/useText';
import { ErrorBox, Section } from 'components';
import { BookingStore } from 'store';
import { OW_FREE_TV_INSTALL_SKU, OFFICEWORKS_FREE_TV_INSTALL_PARENT_ID } from 'helpers/constants';
import styles from '../location.module.scss';

const filterProductsByDevices = (
  products: JsonData,
  selectedDevices?: string[],
  deviceList?: JsonDataItem,
): JsonData => {
  const selectedDeviceIds: number[] = [];

  // Map selected device names to corresponding ids
  selectedDevices?.forEach((selectedDevice) => {
    const foundDevice = deviceList?.find((device: JsonDataItem) => device.name === selectedDevice);
    if (foundDevice) {
      selectedDeviceIds.push(foundDevice.id);
    }
  });

  // Filter products based on matching ids in service_selection
  const filteredProducts = products?.filter((product: JsonDataItem) =>
    isArray(product.service_selection) // skip it when service_selection changed to array even for one value
      ? product.service_selection.some((id: string) => selectedDeviceIds.includes(Number(id)))
      : selectedDeviceIds.includes(Number(product.service_selection)),
  );

  return filteredProducts;
};

type Props = {
  form: FormInstance;
  isLoading?: boolean;
  productListError: JsonDataItem | null;
  refetchProductList: () => void;
  serviceSelectionError: JsonDataItem | null;
  refetchServiceSelection: () => void;
  prepaidProductsIds?: number[];
} & WithOptionalDataTest;

export const ServiceSelection: FC<Props> = observer((props) => {
  const {
    form,
    isLoading,
    productListError,
    refetchProductList,
    serviceSelectionError,
    refetchServiceSelection,
    prepaidProductsIds,
  } = props;
  const { productSelection, notes, callback, setNotes, serviceList, productList } = BookingStore;
  const { getText } = useText();
  const osList = serviceList?.child
    .filter((item: JsonDataItem) => item.name === 'os')
    .flatMap((osItem: JsonDataItem) => osItem.options);
  const selectedDevices = Form.useWatch(['device'], { form });
  const businessOpportunityId = 21;
  const officeworksProductsId = 10;
  const isFreeTVInstallCallback =
    callback?.record?.source === CallbackSource.OWSale &&
    callback?.record?.ow_sale?.sku === OW_FREE_TV_INSTALL_SKU;
  const effectiveOWProductsId = isFreeTVInstallCallback ? OFFICEWORKS_FREE_TV_INSTALL_PARENT_ID : officeworksProductsId;
  Form.useWatch([], { form });

  const filteredProductList = selectedDevices?.length // filter product list according to device selection
    ? filterProductsByDevices(productList || [], selectedDevices, serviceList?.parent.options)
    : [];

  const filteredServiceOptions = serviceList?.parent?.options
    .filter((option: CheckboxOptionType) => {
      const businessOption = Number(option.id) === businessOpportunityId;
      return productSelection?.serviceType === JobServiceType.BusinessOpportunities ? businessOption : !businessOption;
    })
    .map((service: JsonDataItem) => {
      return { label: service.text, value: service.name, standalone: service.standalone };
    });

  useEffect(() => {
    if (notes.length === 0) return;

    const notesString = notes.join('\n');
    const existingDescription = form.getFieldValue('description')
      ? `${form.getFieldValue('description') + '\n' + notesString}`
      : notesString;

    if (!form.getFieldValue('description')?.includes(notesString)) {
      form.setFieldValue('description', existingDescription);
    }
    setNotes([]);
  }, [notes]);

  useEffect(() => {
    form.setFieldValue('os', []); // need to manually reset fields with duplicated 'name'
  }, [selectedDevices]);

  return (
    <Section title="Confirm Job(s) / Confirm We Can Help" loading={isLoading}>
      {/* Device Selection */}
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
        {!serviceSelectionError ? (
          <DynamicField.$
            form={form}
            fieldConfig={{
              ...serviceList?.parent,
              initialValue:
                callback?.record.source === CallbackSource.OWSale || prepaidProductsIds?.length // all prepaid products from the OW products category (for now)
                  ? [
                      serviceList?.parent.options.find((device: JsonDataItem) => device.id === effectiveOWProductsId)
                        ?.name,
                    ]
                  : productSelection?.device,
              required: true,
              rules: [{ required: true }],
              type: 'checkbox-group',
              className: cx(styles.location__formItem_full, styles.location__devices),
              description: (
                <div className="script-box" style={{ marginTop: 16 }}>
                  <Text.$>{getText('booking.location.deviceTypeInquiry')}</Text.$>
                  <Text.$ type="secondary" italic>
                    {getText('booking.location.deviceSelectionInquiry')}
                  </Text.$>
                </div>
              ),
              columns: 2,
              options: filteredServiceOptions,
            }}
          />
        ) : (
          <ErrorBox
            description="Try Service selection lookup again"
            action={refetchServiceSelection}
            actionTitle="Retry"
            error={serviceSelectionError}
          />
        )}
      </Space.$>

      <Divider />

      {/* Device Child Parameters */}
      <DynamicFieldset.$
        key={selectedDevices}
        form={form}
        fields={serviceList?.child.map((item: JsonDataItem) => {
          return {
            ...item,
            initialValue: productSelection?.[item.name],
            type: item.type,
            className: cx(styles.location__formItem_full, styles.location__devices, styles.location__devices_details),
            description: (
              <Text.$ type="secondary" italic>
                {`*${item.additional_header_text}*`}
              </Text.$>
            ),

            required: true,
            rules: [{ required: true }],
            columns: 2,
          };
        })}
        dataTestId="extra"
      />

      {/* Product Selection */}
      {selectedDevices?.length > 0 && (
        <>
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
              <Text.$>
                <Text.$ type="danger">*</Text.$> Service:
              </Text.$>
            </Space.$>

            <Space.$
              direction={Space.Direction.Vertical}
              size={Space.Size.Small}
              className={cx(styles.location__formItem, styles.location__products)}
              fullWidth
            >
              {filteredProductList.length > 0 ? (
                <ProductList
                  onSelect={(productValue) => {
                    form.setFieldValue('products', productValue);
                  }}
                  form={form}
                  products={filteredProductList?.map((product: JsonDataItem) => {
                    return { label: product.name, value: product.ID?.toString(), details: product };
                  })}
                />
              ) : (
                <>
                  {productListError ? (
                    <ErrorBox
                      description="Try Product lookup again"
                      action={refetchProductList}
                      actionTitle="Retry"
                      error={productListError}
                    />
                  ) : (
                    <>No Products for selected devices</>
                  )}
                </>
              )}
            </Space.$>
          </Space.$>
          <Divider />
        </>
      )}

      {/* Extra questions  */}
      {!isString(form.getFieldValue('products')) &&
        form
          .getFieldValue('products')
          ?.map((selectedProduct: JsonDataItem) => (
            <ProductQuestion
              key={selectedProduct.id}
              selectedProduct={selectedProduct}
              productList={productList || []}
              form={form}
            />
          ))}

      {/* Confiramtion */}
      <Text.$ size={Text.Size.MD}>Confirmation:</Text.$>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} className="script-box">
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Text.$>{getText('booking.location.recapIssue')}</Text.$>
          <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.ExtraSmall} wrap>
            {selectedDevices?.map((deviceName: string) => (
              <Tag key={deviceName}>
                {serviceList?.parent?.options?.find((device: JsonDataItem) => device.name === deviceName)?.text}
              </Tag>
            ))}
            {productSelection?.os?.map((osId: string) => (
              <Tag key={osId}>{osList.find((system: JsonDataItem) => system.value === osId)?.label}</Tag>
            ))}
            {isArray(productSelection?.products) &&
              productSelection?.products?.map(
                (item: JsonDataItem) =>
                  !!item.id && (
                    <Tag key={item.id}>
                      {filteredProductList?.find((product) => product.ID.toString() === item.id)?.name} *{item.qty}
                    </Tag>
                  ),
              )}
          </Space.$>
        </Space.$>
        <Text.$ size={Text.Size.SM} mark>
          {getText('booking.location.assuranceMessage')}
        </Text.$>
      </Space.$>
    </Section>
  );
});
